import { allMetricsOptions } from '@intelligence/pages/TopCharts/_config/metricOptions';
import { getPCConsoleMetricValues } from '@intelligence/pages/TopCharts/PCGames/_config/utils';
import type { IAllMetricsSaveParams } from '../types';
import {
  defaultMetricsSaveParams as defaultMetricsSaveParamsNew,
  getDefaultMetricsOptions,
} from '@intelligence/pages/TopCharts/MobileGames/components/GamesDetails/KPITrends/components/MetricsSelection/config';
import { toDateTypeLy } from '@/pages/intelligence/utils/common';
import {
  getPCConsoleMetricDisplayCategory,
  normalizePCConsoleMetricType,
} from '@intelligence/pages/TopCharts/_config/pcConsoleMetricOrdering';

export const FILTERSAVE_TYPE = 'usersave-combine-allmetircs-stats'; // 这个为原有stats，等融合KPITrends的时候，用新的usersave-combine-detail-kpitrends,只存产品想要的

/**
 * 移除指标配置中的列宽设置（仅影响 PC/Console Games Details Stats 页面）
 * @param options 指标配置列表
 * @returns 移除宽度后的配置列表
 */
export const removeColumnWidth = (options: any[]): any[] => {
  return options?.map((option) => {
    const { width, width_cn, ...rest } = option || {};
    return rest;
  });
};

// PC&Console originalStats（AllMetrics）默认选中：Source Steam PCU / ACU + VGI Steam Followers + MScience 线上销量
const ALLMETRICS_ORIGINALSTATS_DEFAULT_METRICS = [
  { value: 'source_steam__pcu', dataSource: 'source_steam' },
  { value: 'source_steam__acu', dataSource: 'source_steam' },
  // { value: 'steamfollowers', dataSource: 'vginsights' },
  { value: 'units', dataSource: 'mscience' },
];

export const defaultMetricList = removeColumnWidth(
  ALLMETRICS_ORIGINALSTATS_DEFAULT_METRICS.map(({ value, dataSource }) =>
    allMetricsOptions?.find(
      (t: any) => t?.value === value && t?.dataSource === dataSource,
    ),
  ).filter(Boolean),
) || [];

export const defaultMetricValues = defaultMetricList?.map((t: any) => t?.value) || [];

/**
 * options选项更新后，计算有效的values，当前values根据options过滤掉options中不存在的value
 * 若过滤后的values大于0，返回过滤后的values
 * 否则返回defaultValues
 * @param payload.options options列表
 * @param payload.defaultValues 默认值
 * @param payload.values 当前已选values
 * @returns
 */
export const calcVisualisMetricsValues = (payload: {
  options: any[];
  defaultValues: string[];
  values: string[];
}): string[] => {
  const { options, defaultValues, values } = payload || {};
  const optionsValues = options?.map((option) => option?.value);
  const validValues = values?.filter((value) => optionsValues?.includes(value));
  const result = validValues?.length ? validValues : defaultValues;
  return Array.isArray(result) ? result : [];
};

export const defaultSourceMetricsApi = getPCConsoleMetricValues(defaultMetricList)?.source_metrics;

/**
 * 生成用于保存用户指标筛选条件的数据
 * @param props.options MetricSelect组件confirm时，返回的勾选指标options
 * @returns 用于保存用户指标筛选条件的数据
 */
export const generateAllMetricsSaveParams = (props: { options: any[] }): IAllMetricsSaveParams => {
  const { options } = props || {};
  const types: Set<string> = new Set();
  const sources: Set<string> = new Set();
  const metrics: Set<string> = new Set();

  options?.forEach((metricOption) => {
    const { segType, dataSourceType, value } = metricOption || {};
    // 保存新的展示分类；实际指标 value/source_metrics 仍按原字段生成，避免改变接口协议。
    const displayType =
      getPCConsoleMetricDisplayCategory(metricOption) ||
      normalizePCConsoleMetricType(segType);
    // 未识别指标不写入空分类，避免污染保存状态；指标本身仍照常保存。
    if (displayType) types.add(displayType);
    sources.add(dataSourceType);
    metrics.add(value);
  });

  return {
    types: Array.from(types),
    sources: Array.from(sources),
    metrics: Array.from(metrics),
    source_metrics: getPCConsoleMetricValues(options)?.source_metrics,
  };
};

export const analysisAllMetricsSaveParamsStr = (res: any): IAllMetricsSaveParams => {
  try {
    const resData = res?.data?.filter((t: any) => t?.name === FILTERSAVE_TYPE);
    if (!Array.isArray(resData) || !resData?.length) {
      return defaultMetricsSaveParams;
    }
    const result = JSON.parse(resData?.[0]?.data);
    const hasValidMetrics = Array.isArray(result?.metrics) && result.metrics.length > 0;
    const hasValidSourceMetrics =
      result?.source_metrics &&
      typeof result.source_metrics === 'object' &&
      Object.keys(result.source_metrics).length > 0;
    // 仅当用户确实保存过有效筛选条件时才使用保存值；否则用当前版本默认值
    if (!hasValidMetrics || !hasValidSourceMetrics) return defaultMetricsSaveParams;
    const selectedOptions = allMetricsOptions.filter((option: any) => result.metrics.includes(option?.value));
    const types = Array.from(
      new Set(
        selectedOptions
          .map((option: any) => getPCConsoleMetricDisplayCategory(option))
          .filter(Boolean),
      ),
    );
    const legacyTypes = Array.isArray(result.types)
      ? result.types
        .map((type: string) => normalizePCConsoleMetricType(type))
        .filter(Boolean)
      : normalizePCConsoleMetricType(result.types);
    return { ...result, types: types.length ? types : legacyTypes };
  } catch (exception) {
    return defaultMetricsSaveParams;
  }
};

export const getDataByValue = (list: any, allMetricsOptionsList: any) => {
  try {
    return allMetricsOptionsList?.filter((element: any) => {
      return list?.includes(element.value);
    });
  } catch (exception) {
    return getDefaultMetricsOptions();
  }
};

// 将options转为source_metrics
export const getObjectByList = (list: any) => {
  const metricTarget: any = {};
  list.forEach((element: any) => {
    const type = element?.metricValue;
    if (metricTarget[type]) {
      metricTarget[type].push(element.value);
    } else {
      metricTarget[type] = [element.value];
    }
  });
  return {
    source_metrics: metricTarget,
  };
};

// 数据重新拼装
const updateFilterDataNew = (resultData: any) => {
  const {
    metrics,
    selectedMetricsValue = 'metrics',
    shotSelectValue = 'all',
    source_metrics,
    sources = ['all'],
    types = 'all',
  } = resultData || defaultMetricsSaveParams;

  const userSaveParams: IAllMetricsSaveParams = {
    sources,
    types,
    metrics,
    source_metrics,
    selectedMetricsValue,
    shotSelectValue,
  };
  return userSaveParams;
};

export const analysisAllMetricsSaveParamsStrNew = (res: any): IAllMetricsSaveParams => {
  try {
    const resData = res?.data?.filter((t: any) => t?.name === FILTERSAVE_TYPE);
    const result = JSON.parse(resData?.[0]?.data);
    // return updateFilterDataNew(result || defaultMetricsSaveParams);
    if (Array.isArray(result?.metrics) && result?.metrics?.length > 0) {
      return result;
    }
    return defaultMetricsSaveParamsNew;
  } catch (exception) {
    return defaultMetricsSaveParamsNew;
  }
};

/** 按 metric value 列表顺序重排 options（用于下载列顺序与指标筛选器一致） */
export const orderMetricsOptionsByValues = (
  options: any[] = [],
  orderedValues: string[] = [],
): any[] => {
  if (!orderedValues?.length) return options;
  const optionMap = new Map(
    options.map((option: any) => [option?.value, option]),
  );
  return orderedValues
    .map(value => optionMap.get(value))
    .filter(Boolean);
};

export const getOptions = (options: any[], metricsSaveParams: IAllMetricsSaveParams): any[] => {
  if (!Array.isArray(options)) return [];
  try {
    const { source_metrics: sourceMetrics, metrics: savedMetrics } =
      metricsSaveParams || {};
    const fromSourceFlat = Object.values(sourceMetrics || {}).flat() as string[];
    // source_metrics 是勾选结果的权威来源；savedMetrics 仅决定顺序。
    // 避免 setSaveParams 尚未提交时，旧 savedMetrics 把新勾选的指标过滤掉。
    const selectedValues = savedMetrics?.length
      ? [
          ...savedMetrics.filter((value: string) => fromSourceFlat.includes(value)),
          ...fromSourceFlat.filter(value => !savedMetrics.includes(value)),
        ]
      : fromSourceFlat;
    const selectedSet = new Set(selectedValues);
    const filtered = options?.filter((option: any) =>
      selectedSet.has(option?.value),
    );
    return orderMetricsOptionsByValues(filtered, selectedValues);
  } catch (err) {
    return getDefaultMetricsOptions();
  }
};

export const getDateScopedAllMetricsOptions = (dateType = ''): any[] => {
  const dateTypeCur = toDateTypeLy[dateType] || dateType;
  return allMetricsOptions.filter((option: any) => {
    if (!Array.isArray(option?.dateEnum) || option.dateEnum.length < 1) return true;
    return option.dateEnum.includes(dateTypeCur);
  });
};

export const getDateScopedAllMetricsSelection = (
  sourceMetrics: any,
  dateType = '',
  metricsOrder: string[] = [],
) => {
  if (
    !sourceMetrics ||
    Array.isArray(sourceMetrics) ||
    typeof sourceMetrics !== 'object'
  ) {
    return {
      metricsOptions: [],
      source_metrics: {},
    };
  }
  const metricsOptions = removeColumnWidth(
    getOptions(getDateScopedAllMetricsOptions(dateType), {
      source_metrics: sourceMetrics,
      metrics: metricsOrder,
    } as IAllMetricsSaveParams),
  );
  const metricsObj = getPCConsoleMetricValues(metricsOptions);
  return {
    metricsOptions,
    source_metrics: metricsObj?.source_metrics || {},
  };
};

/**
 * 按用户勾选的 metric value 列表，重建当前粒度下有效的 source_metrics。
 * 切换日/月粒度时，source_metrics 会被过滤掉不可用的指标（如 DAU），
 * 但 saveParams.metrics 仍保留用户意图；切回日粒度时需从此处恢复。
 */
export const rebuildSourceMetricsFromMetricValues = (
  metricValues: string[] = [],
  dateType = '',
): Record<string, string[]> => {
  if (!Array.isArray(metricValues) || metricValues.length === 0) {
    return {};
  }
  const dateScopedOptions = getDateScopedAllMetricsOptions(dateType);
  const selectedOptions = orderMetricsOptionsByValues(
    dateScopedOptions.filter((option: any) =>
      metricValues.includes(option?.value),
    ),
    metricValues,
  );
  return getPCConsoleMetricValues(selectedOptions)?.source_metrics || {};
};

export const getOptionsFromSourceMetrics = (
  options: any[],
  sourceMetrics: any = {},
  metricsOrder: string[] = [],
): any[] => {
  if (!Array.isArray(options)) return [];
  try {
    return getOptions(options, {
      source_metrics: sourceMetrics,
      metrics: metricsOrder,
    } as IAllMetricsSaveParams);
  } catch (err) {
    return getDefaultMetricsOptions();
  }
};

export const defaultMetricsSaveParams = ((options: any[]) => {
  return generateAllMetricsSaveParams({ options });
})(defaultMetricList);

export const defaultMetricTypeVals = defaultMetricList?.map(
  (t) => getPCConsoleMetricDisplayCategory(t) || normalizePCConsoleMetricType(t.segType),
);
export const defaultSourceVals = defaultMetricList?.map((t) => t.dataSourceType);
export const defaultMetricsVals = defaultMetricList?.map((t) => t?.value);

// 根据时间粒度过滤数据
export const filterByDateType = (list: any, dateType = '', isCompareMetrics = false) => {
  const dateTypeCur = toDateTypeLy[dateType];

  // compare 页有 opinions 舆情指标展示，kpiTrends 页需要将舆情指标过滤掉
  const filterTemp = isCompareMetrics
    ? list
    : [...list]?.filter((item: any) => item?.dataSource !== 'opinions');

  const filterOption = filterTemp?.filter((element: any) =>
    element?.dateEnum?.includes(dateTypeCur),
  );

  return {
    filterOption,
    filterList: filterOption?.map((item: any) => item.value),
  };
};
